import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useContactModal } from '../App';
import { FaStar, FaOm, FaPhone, FaArrowRight, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import services from '../data/services';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { openContactModal } = useContactModal();
  const serviceIndex = services.findIndex(s => s.slug === slug);
  const service = services[serviceIndex];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = service.icon;
  const otherServices = services.filter(s => s.slug !== slug);

  return (
    <div>
      {/* Hero Banner */}
      <section className="page-hero">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(40)].map((_, i) => (
            <div key={i} className="page-hero-star" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
            }} />
          ))}
        </div>
        <div className="page-hero-content">
          <div className="hero-badge-sm"><FaStar /> {service.title}</div>
          <h1>{service.title}</h1>
          <p>{service.desc}</p>
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>/</span> <Link to="/services">Services</Link> <span>/</span> <span>{service.title}</span>
          </div>
        </div>
      </section>

      {/* Service Detail Content */}
      <section className="py-20 bg-warm-off-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
            {/* Main Content */}
            <div>
              <div className="flex items-center gap-5 mb-10">
                <div className="w-[80px] h-[80px] rounded-full flex items-center justify-center text-[2rem] text-white shadow-gold-md" style={{ background: 'linear-gradient(135deg, #8b5cf6, #f97316)' }}>
                  <Icon />
                </div>
                <div>
                  <h2 className="text-3xl max-md:text-2xl text-primary-dark font-heading">{service.title}</h2>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {service.highlights.map((h, i) => (
                      <span key={i} className="py-1 px-3 border border-accent-gold/15 rounded-full text-[0.75rem] text-primary-medium font-medium" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.06), rgba(249,115,22,0.04))' }}>
                        <FaStar className="inline text-accent-saffron text-[0.55rem] mr-1" />{h}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[20px] p-10 max-md:p-6 border border-warm-light-gray">
                {service.longDesc.map((para, i) => (
                  <p key={i} className="text-[0.95rem] text-warm-text-body leading-[1.85] mb-5 last:mb-0">{para}</p>
                ))}
              </div>

              {/* Benefits */}
              <div className="mt-10 bg-white rounded-[20px] p-10 max-md:p-6 border border-warm-light-gray">
                <h3 className="text-xl text-primary-dark font-heading mb-6">Key Benefits</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-accent-gold/10 transition-all duration-300 hover:border-accent-gold/25 hover:bg-accent-gold/[0.03]">
                      <FaCheckCircle className="text-accent-saffron text-sm mt-0.5 flex-shrink-0" />
                      <span className="text-[0.88rem] text-warm-text-body leading-[1.6]">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Box */}
              <div className="mt-10 rounded-[20px] p-10 max-md:p-6 text-center" style={{ background: 'linear-gradient(135deg, #151136, #221c4a)' }}>
                <FaOm className="text-[2rem] text-accent-gold mb-3 mx-auto" />
                <h3 className="text-xl text-white font-heading mb-2">Ready for {service.title}?</h3>
                <p className="text-[0.9rem] text-white/70 mb-6">Get personalized cosmic guidance from Acharya Arun</p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <a href="tel:+918084768303" className="btn-primary"><FaPhone /> Call Now</a>
                  <button onClick={openContactModal} className="btn-secondary !border-white/30 !text-white hover:!bg-transparent hover:!border-accent-gold">
                    Book Appointment <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <div className="bg-white rounded-[20px] p-6 border border-warm-light-gray">
                <h4 className="text-lg text-primary-dark font-heading mb-4">All Services</h4>
                <div className="flex flex-col gap-2">
                  {services.map((s) => {
                    const SIcon = s.icon;
                    const isActive = s.slug === slug;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className={`flex items-center gap-3 py-3 px-4 rounded-xl text-[0.88rem] font-medium transition-all duration-300 ${isActive ? 'text-white shadow-gold-sm' : 'text-primary-medium hover:bg-accent-gold/[0.06] hover:text-primary-dark'}`}
                        style={isActive ? { background: 'linear-gradient(135deg, #8b5cf6, #f97316)' } : {}}
                      >
                        <SIcon className={`text-base flex-shrink-0 ${isActive ? 'text-white' : 'text-accent-gold'}`} />
                        {s.title}
                      </Link>
                    );
                  })}
                </div>
              </div>

              <Link to="/services" className="flex items-center gap-2 justify-center mt-5 py-3 px-5 rounded-xl border border-warm-light-gray text-[0.88rem] text-primary-medium font-medium transition-all duration-300 hover:border-accent-gold/30 hover:text-primary-dark bg-white">
                <FaArrowLeft className="text-xs" /> View All Services
              </Link>

              {/* Profile Image */}
              <div className="mt-6 relative service-profile-wrapper hidden lg:block">
                <div className="absolute -inset-3 rounded-[24px] border border-accent-gold/10 animate-spin-slow" />
                <div className="absolute -inset-6 rounded-[28px] border border-accent-gold/[0.05]" style={{ animation: 'spin-slow 50s linear infinite reverse' }} />
                <div className="relative rounded-[20px] overflow-hidden service-profile-frame">
                  <img src="/assets/profile-img-1.png" alt="Acharya Arun" className="w-full h-[400px] object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/70 via-primary-deep/10 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-deep/15 via-transparent to-primary-deep/15" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                    <span className="font-heading text-base text-accent-gold tracking-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">Acharya Arun</span>
                    <span className="block text-[0.7rem] text-white/60 mt-0.5 tracking-wider uppercase">Vedic Astrologer & Cosmic Healer</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
